import { formatDistanceToNow, format } from 'date-fns';
import { getFeaturedPhotoUrl } from '../../components/ListingPreview/ListingPreviewModal';
import { getListingName } from './listingDisplay';
import { HOST_AI_FIELD_KEYS } from './constants';
import { isInsuranceAddressed } from '../../utils/insuranceHelpers';
import { hasAvailabilityOverlap } from '../../utils/availabilitySchedule';

export {
    getDisplay,
    getListingName,
    getListingType,
    getListingAddress,
    getRowSpots,
    getRowRate,
    getInstructions,
} from './listingDisplay';

export const GROUPS = [
    { key: 'bulk_draft', title: 'Drafts From Bulk Upload', marker: 'bulk', filter: p => p._v5Status === 'bulk_draft' },
    { key: 'attention', title: 'Needs your attention', marker: 'attention', filter: p => p._v5Status === 'attention' },
    { key: 'review', title: 'In review', marker: 'review', filter: p => p._v5Status === 'review' },
    { key: 'live', title: 'Live', marker: 'live', filter: p => p._v5Status === 'live' },
    { key: 'paused', title: 'Paused', marker: 'paused', filter: p => p._v5Status === 'paused' },
    // PP-403 — paused-with-edits is its own group so the "All" view can
    // distinguish the two sub-states. The Paused tab (PROPS_TABS below)
    // matches BOTH.
    { key: 'paused_review', title: 'Paused — Needs review', marker: 'paused_review', filter: p => p._v5Status === 'paused_review' },
    { key: 'draft', title: 'Drafts', marker: 'draft', filter: p => p._v5Status === 'draft' },
];

export const PROPS_TABS = [
    { key: 'all', label: 'All' },
    { key: 'bulk_draft', label: 'Bulk Drafts' },
    { key: 'attention', label: 'Needs your attention' },
    { key: 'review', label: 'In review' },
    { key: 'live', label: 'Live' },
    // PP-403 — Paused tab includes both paused sub-states; see tabFilter().
    { key: 'paused', label: 'Paused' },
    { key: 'draft', label: 'Single drafts' },
];

// PP-403 — tab → filter predicate. The Paused tab is the one tab that
// matches more than one _v5Status (paused + paused_review). Other tabs
// match exactly by key. Use this everywhere a UI consumer filters by
// the selected tab.
export function listingMatchesTab(listing, tabKey) {
    if (tabKey === 'all') return listing._v5Status != null;
    if (tabKey === 'paused') return listing._v5Status === 'paused' || listing._v5Status === 'paused_review';
    return listing._v5Status === tabKey;
}

export const STATUS_FILTER_OPTIONS = [
    'All statuses',
    'Pending Review',
    'In Review',
    'Approved (Live)',
    'Rejected',
];

export const SORT_OPTIONS = ['Most recent', 'A → Z'];

export function mapDbStatusToV5Status(listing) {
    if (listing.status === 'live' && listing.pausedAt) {
        // PP-403 — split paused into two sub-states.
        // pausedHasEdits=true → host has saved a PUT since pausing and
        // must submit those changes for review before resuming.
        return listing.pausedHasEdits ? 'paused_review' : 'paused';
    }
    switch (listing.status) {
        case 'live':     return 'live';
        case 'approved': return 'review';
        case 'pending':  return 'review';
        case 'denied':   return 'attention';
        case 'archived': return null;
        case 'draft':    return (listing.source === 'bulk' || listing.bulkJobId) ? 'bulk_draft' : 'draft';
        default:         return 'draft';
    }
}

export function enrichListing(listing) {
    const _v5Status = mapDbStatusToV5Status(listing);
    if (!_v5Status) return null;
    return { ...listing, _v5Status };
}

export function getRowPhotoUrl(listing) {
    return getFeaturedPhotoUrl(listing.photos);
}

function effectiveTypeValue(listing) {
    const cf = listing.confirmedFields?.type;
    if (cf != null && String(cf).trim() !== '') return cf;
    const uf = listing.uploadFields?.type;
    if (uf != null && String(uf).trim() !== '') return uf;
    const ef = listing.enrichmentFields?.type;
    const efVal = ef?.value ?? (typeof ef === 'string' ? ef : null);
    if (efVal != null && String(efVal).trim() !== '') return efVal;
    return null;
}

export function bulkDraftMissingRequired(listing) {
    const missing = [];
    if (effectiveTypeValue(listing) == null) missing.push('Type');
    if (listing.pricing?.hourlyRate == null) missing.push('Hourly rate');
    if (listing.pricing?.maxSpots == null) missing.push('Spots');
    return missing;
}

export function getBulkCheckState(listing) {
    const photos = listing.photos?.length ?? 0;
    const enrichment = listing.enrichmentFields || {};
    const confirmed = listing.confirmedFields || {};
    let aiPending = 0;

    for (const key of HOST_AI_FIELD_KEYS) {
        const enrichField = enrichment[key];
        if (enrichField == null) continue;
        const rawValue = enrichField?.value ?? enrichField;
        if (rawValue == null || rawValue === '') continue;

        if (confirmed[key] == null || confirmed[key] === '') aiPending += 1;
    }

    const hasAiPhotos = (listing.photos || []).some((p) => p.source === 'ai' || p.source === 'places');
    return {
        photos,
        photoSource: photos > 0 ? (hasAiPhotos ? 'ai' : 'host') : null,
        geofenceReviewed: Array.isArray(listing.geofence) && listing.geofence.length > 0,
        aiPending,
        insuranceAddressed: isInsuranceAddressed(listing.insurance),
        availabilityValid: !hasAvailabilityOverlap(listing.confirmedFields?.availability),
    };
}

/** Core bulk readiness including additionally-insured legal entity capture. */
export function bulkDraftCoreReady(listing) {
    if (bulkDraftMissingRequired(listing).length > 0) return false;
    const c = getBulkCheckState(listing);
    return c.geofenceReviewed && c.aiPending === 0 && c.photos > 0;
}

export function bulkDraftReady(listing) {
    const c = getBulkCheckState(listing);
    return bulkDraftCoreReady(listing) && c.insuranceAddressed && c.availabilityValid;
}

export function isSubmittableBulkDraft(listing) {
    return bulkDraftReady(listing);
}

export function matchesStatusFilter(listing, filterLabel) {
    switch (filterLabel) {
        case 'Pending Review':
            return listing.status === 'pending';
        case 'In Review':
            return listing._v5Status === 'review';
        case 'Approved (Live)':
            return listing._v5Status === 'live';
        case 'Rejected':
            return listing._v5Status === 'attention';
        default:
            return true;
    }
}

function listingSortDate(listing) {
    return new Date(
        listing.submittedAt
        || listing.updatedAt
        || listing.createdAt
    ).getTime();
}

export function sortListings(listings, sortLabel) {
    const sorted = [...listings];
    if (sortLabel === 'A → Z') {
        sorted.sort((a, b) => getListingName(a).localeCompare(getListingName(b)));
    } else {
        sorted.sort((a, b) => listingSortDate(b) - listingSortDate(a));
    }
    return sorted;
}

export function groupListings(listings, filterKey = 'all', sortLabel = 'Most recent') {
    const enriched = listings.map(enrichListing).filter(Boolean);
    const sorted = sortListings(enriched, sortLabel);

    if (filterKey === 'all') {
        return GROUPS.map(g => ({
            ...g,
            rows: sorted.filter(g.filter),
        })).filter(g => g.rows.length > 0);
    }

    // PP-403 — the Paused tab combines both paused sub-states ("Paused"
    // and "Paused — Needs review") under a single tab. Other tabs match a
    // single GROUP by key.
    if (filterKey === 'paused') {
        const rows = sorted.filter((l) => listingMatchesTab(l, 'paused'));
        if (!rows.length) return [];
        return [{
            key: 'paused',
            title: 'Paused',
            marker: 'paused',
            rows,
            hideHead: true,
        }];
    }

    const group = GROUPS.find(g => g.key === filterKey);
    if (!group) return [];

    const rows = sorted.filter(group.filter);
    if (!rows.length) return [];

    return [{
        ...group,
        rows,
        hideHead: true,
    }];
}

export function formatRelative(date) {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return formatDistanceToNow(d, { addSuffix: true });
}

export function formatLiveSince(date) {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return format(d, 'MMM d');
}

export function getRowMeta(listing) {
    const v5Status = listing._v5Status;
    if (v5Status === 'live') {
        return `Live since ${formatLiveSince(listing.reviewedAt || listing.updatedAt)}`;
    }
    if (v5Status === 'paused') {
        return `Paused ${formatRelative(listing.pausedAt || listing.updatedAt)}`;
    }
    if (v5Status === 'draft') {
        return `Updated ${formatRelative(listing.updatedAt)}`;
    }
    if (v5Status === 'bulk_draft') {
        return `Uploaded ${formatRelative(listing.createdAt)}`;
    }
    return `Submitted ${formatRelative(listing.submittedAt || listing.updatedAt)}`;
}

export function hasGeocodeWarning(listing) {
    return listing.addressData?.geocode === 'warn';
}

export function getStatusPillMeta(listing) {
    if (listing.status === 'live' && listing.pausedAt) {
        // PP-403 — distinct pill copy for the two paused sub-states so the
        // host can tell at-a-glance which one needs action.
        return listing.pausedHasEdits
            ? { className: 'paused_review', label: 'Paused — Needs review' }
            : { className: 'paused', label: 'Paused' };
    }
    switch (listing.status) {
        case 'live':
            return { className: 'approved', label: 'Live' };
        case 'denied':
            return { className: 'rejected', label: 'Rejected' };
        case 'pending':
            return { className: 'pending', label: 'Pending Review' };
        case 'approved':
            return { className: 'review', label: 'In Review' };
        case 'draft':
            if (listing.source === 'bulk' || listing.bulkJobId) {
                return { className: 'review', label: 'Drafts from bulk upload' };
            }
            return { className: 'draft', label: 'Draft' };
        default:
            return { className: 'draft', label: 'Draft' };
    }
}

export function showEditButton(listing) {
    if (listing.source === 'bulk' && listing.status === 'draft') return true;
    const v5 = listing._v5Status;
    return v5 === 'attention' || v5 === 'draft';
}

export function getEditButtonLabel(listing, editMode = false) {
    if (listing.source === 'bulk' && listing.status === 'draft') {
        return editMode ? 'Editing — click to cancel' : 'Edit listing';
    }
    const v5 = listing._v5Status;
    if (v5 === 'attention') return 'Edit & Resubmit →';
    return 'Resume draft →';
}
